import { Badge, Box, CircularProgress, Stack, Switch, Tooltip } from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';

import { SEP_AMBER, SEP_BLUE, SEP_RED } from '@/theme';
import type { AppSummary, LicenseCellState } from '@/types/api';
import { describeCell, isExpiringSoon } from './cellState';

interface LicenseCellSwitchProps {
  app: AppSummary;
  tenantName: string;
  cell: LicenseCellState;
  saving: boolean;
  failed: boolean;
  onToggle: (next: boolean) => void;
}

export function LicenseCellSwitch({
  app,
  tenantName,
  cell,
  saving,
  failed,
  onToggle,
}: LicenseCellSwitchProps) {
  const state = describeCell(app, cell);
  const overrides = cell?.overrideCount ?? { allow: 0, deny: 0 };
  const hasOverrides = overrides.allow > 0 || overrides.deny > 0;

  const overrideTitle = [
    overrides.allow > 0 ? `${overrides.allow} Benutzer-Ausnahme(n): erlaubt` : null,
    overrides.deny > 0 ? `${overrides.deny} Benutzer-Ausnahme(n): gesperrt` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  if (saving) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: 58 }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  const control = (
    <Switch
      size="small"
      checked={state.checked}
      disabled={!state.interactive}
      onChange={(event) => onToggle(event.target.checked)}
      // Naming both axes: a grid of unlabelled switches is unusable with a
      // screen reader.
      slotProps={{ input: { 'aria-label': `${app.name} für Mandant ${tenantName}` } }}
      sx={{
        ...(state.variant === 'expired' && {
          '& .MuiSwitch-thumb': { backgroundColor: SEP_AMBER },
        }),
        ...(failed && {
          outline: `2px solid ${SEP_RED}`,
          borderRadius: 1,
        }),
      }}
    />
  );

  const withOverrides = hasOverrides ? (
    <Tooltip title={overrideTitle}>
      <Badge
        badgeContent={overrides.allow + overrides.deny}
        // Blue for "allowed beyond the tenant license", red for "explicitly
        // blocked" — the same colours the rest of the app uses for those ideas.
        sx={{
          '& .MuiBadge-badge': {
            backgroundColor: overrides.deny > 0 ? SEP_RED : SEP_BLUE,
            color: '#fff',
            fontSize: 10,
            height: 16,
            minWidth: 16,
          },
        }}
      >
        {control}
      </Badge>
    </Tooltip>
  ) : (
    control
  );

  return (
    <Tooltip title={state.tooltip}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'center', width: 58 }}>
        {withOverrides}
        {isExpiringSoon(cell) && (
          <ScheduleOutlinedIcon sx={{ fontSize: 14, color: SEP_AMBER, ml: 0.25 }} />
        )}
      </Stack>
    </Tooltip>
  );
}
